import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  IconButton,
  InputAdornment,
  Avatar,
  Container,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EuroIcon from '@mui/icons-material/Euro';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { getAllProdotti } from '../APIs/ProductsAPI';
import { createPackage, deletePackageById, getAllPackages, updatePackage, uploadPackageImage } from '../APIs/PackagesAPI';
import { getTenantTranslation } from '../utils/getTenantTranslation';
import PackageFormDialog from '../components/allestimenti/PackageFormDialog';

const emptyPackageForm = {
  id: null,
  name: '',
  description: '',
  imagePaths: [],
  capacity: '',
  active: true,
  activeFrom: '00:00',
  activeTo: '23:59',
  manualPrice: '',
  type: 'STANDARD',
  items: [],
};

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMoney(value) {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(toNumber(value));
}

function toMinutes(timeStr) {
  const [h, m] = String(timeStr || '00:00').split(':').map(Number);
  return (h * 60) + m;
}

function isNowInRange(from, to) {
  const now = new Date();
  const nowMinutes = (now.getHours() * 60) + now.getMinutes();
  const fromMinutes = toMinutes(from);
  const toMinutesValue = toMinutes(to);

  if (toMinutesValue >= fromMinutes) {
    return nowMinutes >= fromMinutes && nowMinutes <= toMinutesValue;
  }
  return nowMinutes >= fromMinutes || nowMinutes <= toMinutesValue;
}

function normalizeApiProducts(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => ({
    id: String(p.id),
    name: p.name || `Prodotto #${p.id}`,
    price: toNumber(p.price),
    categoryId: p.categoryId ? String(p.categoryId) : '',
    categoryName: p.category?.name || '',
    subcategoryId: p.subcategoryId ? String(p.subcategoryId) : '',
    subcategoryName: p.subcategory?.name || '',
    photoPath: p.photoPath || '',
  }));
}

function normalizeApiPackages(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((pkg) => ({
    id: pkg.id,
    name: pkg.name || '',
    description: pkg.description || '',
    imagePaths: Array.isArray(pkg.imagePaths)
      ? pkg.imagePaths.filter((item) => typeof item === 'string' && item.trim() !== '').map((item) => item.trim())
      : [],
    price: toNumber(pkg.price),
    active: Array.isArray(pkg.visibility) ? pkg.visibility.length > 0 : true,
    activeFrom: Array.isArray(pkg.visibility) && pkg.visibility[0]?.startTime ? pkg.visibility[0].startTime : '00:00',
    activeTo: Array.isArray(pkg.visibility) && pkg.visibility[0]?.endTime ? pkg.visibility[0].endTime : '23:59',
    manualPrice: pkg.manualPrice ?? toNumber(pkg.price),
    capacity: pkg.capacity ?? '',
    type: pkg.type === 'TASTING_MENU' ? 'TASTING_MENU' : 'STANDARD',
    items: Array.isArray(pkg.items)
      ? pkg.items.map((item, index) => ({
        clientKey: `package-item-${pkg.id}-${item.id || index}`,
        productId: item.productId,
        variantId: item.variantId ?? null,
        quantity: toNumber(item.quantity) || 1,
        courseIndex: Math.max(1, Math.trunc(toNumber(item.courseIndex) || 1)),
        courseName: item.courseName || '',
        sortOrder: Number.isInteger(Number(item.sortOrder)) ? Number(item.sortOrder) : index,
      }))
      : [],
    updatedAt: pkg.updatedAt || null,
  }));
}

const GestioneAllestimentiPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const tenantId = localStorage.getItem('tenantId') || 'default';
  const tenantTypeRaw = localStorage.getItem('tenantType') || '';
  const token = localStorage.getItem('accessToken');
  const gt = (key) => getTenantTranslation(t, tenantTypeRaw, key);

  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState('');

  const [openPackageForm, setOpenPackageForm] = useState(false);
  const [packageForm, setPackageForm] = useState(emptyPackageForm);
  const [deletePackageId, setDeletePackageId] = useState(null);
  const [uploadingPackageImages, setUploadingPackageImages] = useState(false);

  const fetchProducts = useCallback(async () => {
    try {
      const data = await getAllProdotti(token, tenantId);
      setProducts(normalizeApiProducts(data));
    } catch {
      setProducts([]);
    }
  }, [tenantId, token]);

  const fetchPackages = useCallback(async () => {
    try {
      const data = await getAllPackages(token, tenantId);
      setPackages(normalizeApiPackages(data));
    } catch {
      setPackages([]);
    }
  }, [tenantId, token]);

  useEffect(() => {
    fetchProducts();
    fetchPackages();
  }, [fetchPackages, fetchProducts]);

  useEffect(() => {
    const onProductCreated = () => {
      fetchProducts();
    };
    const onFocus = () => {
      fetchProducts();
      fetchPackages();
    };
    window.addEventListener('prodottoCreato', onProductCreated);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('prodottoCreato', onProductCreated);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchPackages, fetchProducts]);

  const productById = useMemo(
    () => Object.fromEntries(products.map((p) => [String(p.id), p])),
    [products]
  );

  const filteredPackages = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return packages;

    return packages.filter((pkg) => {
      const itemNames = (pkg.items || [])
        .map((item) => productById[String(item.productId)]?.name || '')
        .join(' ')
        .toLowerCase();

      return (
        (pkg.name || '').toLowerCase().includes(q) ||
        (pkg.description || '').toLowerCase().includes(q) ||
        itemNames.includes(q)
      );
    });
  }, [packages, search, productById]);

  const totalActive = packages.filter((item) => item.active).length;
  const totalInactive = packages.filter((item) => !item.active).length;

  const selectedProducts = useMemo(() => {
    return (packageForm.items || [])
      .map((item) => {
        const product = productById[String(item.productId)];
        if (!product) return null;
        return {
          ...item,
          product,
          lineTotal: toNumber(item.quantity) * toNumber(product.price),
        };
      })
      .filter(Boolean);
  }, [packageForm.items, productById]);

  const selectedProductsTotal = useMemo(
    () => selectedProducts.reduce((sum, item) => sum + item.lineTotal, 0),
    [selectedProducts]
  );

  const computedPackagePrice =
    String(packageForm.manualPrice).trim() === ''
      ? selectedProductsTotal
      : toNumber(packageForm.manualPrice);

  const openCreatePackageModal = () => {
    setPackageForm(emptyPackageForm);
    setOpenPackageForm(true);
  };

  const openEditPackageModal = (pkg) => {
    setPackageForm({
      id: pkg.id,
      name: pkg.name || '',
      description: pkg.description || '',
      imagePaths: Array.isArray(pkg.imagePaths) ? pkg.imagePaths : [],
      capacity: pkg.capacity || '',
      active: Array.isArray(pkg.visibility) ? pkg.visibility.length > 0 : Boolean(pkg.active),
      activeFrom: pkg.activeFrom || '00:00',
      activeTo: pkg.activeTo || '23:59',
      manualPrice: pkg.manualPrice ?? '',
      type: pkg.type === 'TASTING_MENU' ? 'TASTING_MENU' : 'STANDARD',
      items: Array.isArray(pkg.items) ? pkg.items : [],
    });
    setOpenPackageForm(true);
  };

  const closePackageModal = () => {
    setOpenPackageForm(false);
    setPackageForm(emptyPackageForm);
  };

  const handlePackageChange = (field, value) => {
    setPackageForm((prev) => {
      if (field !== 'type' || value !== 'STANDARD') {
        return { ...prev, [field]: value };
      }

      const mergedItems = Array.from(
        (prev.items || []).reduce((itemsByProduct, item) => {
          const key = `${item.productId}-${item.variantId || ''}`;
          const existing = itemsByProduct.get(key);
          if (existing) {
            existing.quantity += toNumber(item.quantity) || 1;
          } else {
            itemsByProduct.set(key, {
              ...item,
              courseIndex: 1,
              courseName: '',
            });
          }
          return itemsByProduct;
        }, new Map()).values()
      );

      return { ...prev, type: value, items: mergedItems };
    });
  };

  const togglePackageProduct = (productId, courseIndex = 1, courseName = '') => {
    const id = String(productId);
    const normalizedCourseIndex = Math.max(1, parseInt(courseIndex || '1', 10) || 1);
    setPackageForm((prev) => {
      const existingItem = (prev.items || []).find((item) =>
        String(item.productId) === id &&
        (prev.type !== 'TASTING_MENU' || Number(item.courseIndex || 1) === normalizedCourseIndex)
      );
      if (existingItem) {
        return {
          ...prev,
          items: prev.items.filter((item) => item.clientKey !== existingItem.clientKey),
        };
      }
      return {
        ...prev,
        items: [
          ...(prev.items || []),
          {
            clientKey: `package-item-new-${Date.now()}-${Math.random()}`,
            productId,
            quantity: 1,
            courseIndex: prev.type === 'TASTING_MENU' ? normalizedCourseIndex : 1,
            courseName: prev.type === 'TASTING_MENU' ? courseName : '',
            sortOrder: (prev.items || []).length,
          },
        ],
      };
    });
  };

  const updatePackageProductField = (clientKey, field, value) => {
    setPackageForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((item) =>
        item.clientKey === clientKey
          ? {
            ...item,
            [field]: field === 'quantity' || field === 'courseIndex'
              ? Math.max(1, parseInt(value || '1', 10) || 1)
              : value,
          }
          : item
      ),
    }));
  };

  const updatePackageCourse = (courseIndex, field, value) => {
    const normalizedCourseIndex = Math.max(1, Number(courseIndex) || 1);
    setPackageForm((prev) => ({
      ...prev,
      items: (prev.items || []).map((item) =>
        Number(item.courseIndex || 1) === normalizedCourseIndex
          ? { ...item, [field]: value }
          : item
      ),
    }));
  };

  const uploadPackageImagesFiles = async (files) => {
    const fileList = Array.from(files || []);
    if (fileList.length === 0) return;
    setUploadingPackageImages(true);
    try {
      const uploaded = [];
      for (const file of fileList) {
        const result = await uploadPackageImage(file, token, tenantId);
        if (typeof result?.path === 'string' && result.path.trim()) {
          uploaded.push(result.path.trim());
        }
      }
      if (uploaded.length > 0) {
        setPackageForm((prev) => ({
          ...prev,
          imagePaths: [...new Set([...(prev.imagePaths || []), ...uploaded])],
        }));
      }
    } catch (error) {
      console.error('Errore upload immagini pacchetto:', error);
    } finally {
      setUploadingPackageImages(false);
    }
  };

  const removePackageImage = (pathToRemove) => {
    if (!pathToRemove) return;
    setPackageForm((prev) => ({
      ...prev,
      imagePaths: (prev.imagePaths || []).filter((item) => item !== pathToRemove),
    }));
  };

  const savePackage = async () => {
    if (!packageForm.name.trim()) return;
    if (!Array.isArray(packageForm.items) || packageForm.items.length === 0) return;

    const payload = {
      name: packageForm.name.trim(),
      description: packageForm.description.trim(),
      imagePaths: Array.isArray(packageForm.imagePaths)
        ? packageForm.imagePaths
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter(Boolean)
        : [],
      price: computedPackagePrice,
      type: packageForm.type === 'TASTING_MENU' ? 'TASTING_MENU' : 'STANDARD',
      visibility: packageForm.active
        ? [{ startTime: packageForm.activeFrom || '00:00', endTime: packageForm.activeTo || '23:59' }]
        : [],
      capacity: String(packageForm.capacity).trim() === '' ? null : toNumber(packageForm.capacity),
      manualPrice: String(packageForm.manualPrice).trim() === '' ? null : toNumber(packageForm.manualPrice),
      items: packageForm.items.map((item, index) => ({
        productId: toNumber(item.productId),
        variantId: item.variantId ? toNumber(item.variantId) : null,
        quantity: toNumber(item.quantity) || 1,
        courseIndex: packageForm.type === 'TASTING_MENU'
          ? Math.max(1, Math.trunc(toNumber(item.courseIndex) || 1))
          : 1,
        courseName: packageForm.type === 'TASTING_MENU'
          ? (String(item.courseName || '').trim() || null)
          : null,
        sortOrder: index,
      })),
    };

    try {
      if (packageForm.id) {
        await updatePackage(packageForm.id, payload, token, tenantId);
      } else {
        await createPackage(payload, token, tenantId);
      }
      await fetchPackages();
      closePackageModal();
    } catch {
      // keep modal open so user can retry
    }
  };

  const deletePackage = async () => {
    if (!deletePackageId) return;
    try {
      await deletePackageById(deletePackageId, token, tenantId);
      await fetchPackages();
      setDeletePackageId(null);
    } catch {
      // keep dialog open so user can retry
    }
  };

  return (
    <Container className="admin-responsive-page" maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, backgroundColor: theme.palette.background.default }}>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              color: theme.palette.primary.main,
              mb: 1,
            }}
          >
            {gt('allestimenti.pageTitle')}
          </Typography>

        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
              {gt('allestimenti.summaryTotalPackages')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.primary.main }}>
              {packages.length}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
              {gt('allestimenti.summaryActivePackages')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {totalActive}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
              {gt('allestimenti.summaryInactivePackages')}
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800 }}>
              {totalInactive}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
        }}
      >
        <TextField
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={gt('allestimenti.searchPlaceholder')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: { xs: '100%', md: 420 },
            '& .MuiOutlinedInput-root': { borderRadius: 3 },
          }}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreatePackageModal}
          disabled={products.length === 0}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          {gt('allestimenti.newPackage')}
        </Button>
      </Paper>

      <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
          {gt('allestimenti.productsAvailable')}
        </Typography>

        {products.length === 0 ? (
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: `1px dashed ${theme.palette.divider}`, textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
              {gt('allestimenti.noProductsAvailable')}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              {gt('allestimenti.noProductsAvailableHint')}
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={1.5}>
            {products.map((p) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
                <Paper elevation={0} sx={{ p: 1.3, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.default }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Avatar src={p.photoPath || ''} alt={p.name} variant="rounded" sx={{ width: 48, height: 48 }}>
                      {p.name?.[0]?.toUpperCase() || '?'}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
                        {p.name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: theme.palette.text.secondary }} noWrap>
                        {p.subcategoryName ? `${p.categoryName} - ${p.subcategoryName}` : p.categoryName}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: theme.palette.primary.main, fontWeight: 700 }}>
                        {formatMoney(p.price)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      <Grid container spacing={3}>
        {filteredPackages.map((pkg) => {
          const activeNow = Boolean(pkg.active) && isNowInRange(pkg.activeFrom, pkg.activeTo);
          const includedItems = (pkg.items || [])
            .map((item) => {
              const product = productById[String(item.productId)];
              if (!product) return null;
              return { ...item, product };
            })
            .filter(Boolean);

          return (
            <Grid item xs={12} md={6} lg={4} key={pkg.id}>
              <Card elevation={0} sx={{ height: '100%', borderRadius: 5, border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
                    <Box>
                      <Chip
                        label={pkg.type === 'TASTING_MENU'
                          ? gt('allestimenti.packageTypeTastingMenu')
                          : gt('allestimenti.packageTypeStandard')}
                        size="small"
                        color={pkg.type === 'TASTING_MENU' ? 'primary' : 'default'}
                        sx={{ mb: 1, fontWeight: 700 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 800, color: theme.palette.text.primary, mb: 0.5 }}>
                        {pkg.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: theme.palette.text.secondary, minHeight: 42 }}>
                        {pkg.description || '-'}
                      </Typography>
                    </Box>

                    <Chip
                      label={!pkg.active ? gt('allestimenti.packageStatusInactive') : activeNow ? gt('allestimenti.packageStatusActiveNow') : gt('allestimenti.packageStatusOutOfTime')}
                      size="small"
                      color={!pkg.active ? 'warning' : activeNow ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Stack>

                  <Stack spacing={1.2} sx={{ mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EuroIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      <Typography variant="body2">{gt('allestimenti.packagePrice')}: <strong>{formatMoney(pkg.price)}</strong></Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EventSeatIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      <Typography variant="body2">{gt('allestimenti.packageCapacity')}: <strong>{toNumber(pkg.capacity)} {gt('allestimenti.packageCapacityPeople')}</strong></Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccessTimeIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      <Typography variant="body2">{gt('allestimenti.packageTimeRange')}: <strong>{pkg.activeFrom || '00:00'} - {pkg.activeTo || '23:59'}</strong></Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Inventory2OutlinedIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                      <Typography variant="body2">{gt('allestimenti.includedProducts')}: <strong>{includedItems.length}</strong></Typography>
                    </Stack>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.2 }}>
                      {gt('allestimenti.packageContent')}
                    </Typography>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {includedItems.map((item) => (
                        <Chip
                          key={`${pkg.id}-${item.productId}-${item.sortOrder}`}
                          label={`${pkg.type === 'TASTING_MENU'
                            ? `${item.courseIndex}${item.courseName ? ` ${item.courseName}` : ''}: `
                            : ''}${item.product.name} x${item.quantity}`}
                          size="small"
                        />
                      ))}
                    </Stack>
                  </Box>
                </CardContent>

                <CardActions sx={{ px: 3, pb: 2.5, pt: 0, justifyContent: 'flex-end' }}>
                  <IconButton onClick={() => openEditPackageModal(pkg)} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2.5 }}>
                    <EditOutlinedIcon />
                  </IconButton>
                  <IconButton onClick={() => setDeletePackageId(pkg.id)} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2.5, color: '#b00508' }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {filteredPackages.length === 0 && (
        <Paper elevation={0} sx={{ mt: 3, p: 4, textAlign: 'center', borderRadius: 4, border: `1px dashed ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            {gt('allestimenti.noPackagesFound')}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {gt('allestimenti.noPackagesHint')}
          </Typography>
        </Paper>
      )}

      <PackageFormDialog
        open={openPackageForm}
        onClose={closePackageModal}
        packageForm={packageForm}
        onFieldChange={handlePackageChange}
        products={products}
        onToggleProduct={togglePackageProduct}
        onUpdateProductField={updatePackageProductField}
        onUpdateCourse={updatePackageCourse}
        selectedProducts={selectedProducts}
        selectedProductsTotal={selectedProductsTotal}
        computedPackagePrice={computedPackagePrice}
        onSave={savePackage}
        gt={gt}
        formatMoney={formatMoney}
        theme={theme}
        onUploadImages={uploadPackageImagesFiles}
        onRemoveImage={removePackageImage}
        uploadingImages={uploadingPackageImages}
      />

      <Dialog open={Boolean(deletePackageId)} onClose={() => setDeletePackageId(null)} PaperProps={{ sx: { borderRadius: 4, p: 1, backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{gt('allestimenti.deletePackageTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{gt('allestimenti.deletePackageConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePackageId(null)} sx={{ textTransform: 'none', fontWeight: 700 }}>{gt('allestimenti.cancel')}</Button>
          <Button variant="contained" color="error" onClick={deletePackage} sx={{ textTransform: 'none', fontWeight: 700 }}>{gt('allestimenti.delete')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GestioneAllestimentiPage;
